import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Image as ImageIcon, Link as LinkIcon, Compass, PhoneCall, UserPlus, 
  Trash2, Plus, LogOut, CheckCircle, ClipboardList, Eye, X, Edit3, Download, Loader2, MessageSquare, Key
} from 'lucide-react';
import ImageUploadCompressor from '../Components/ImageUploadCompressor';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Applications');

  // Loading States
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Shared Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Bottom-Right Toast State
  const [toast, setToast] = useState(null);

  // Lists
  const [notices, setNotices] = useState([]);
  const [galleryCards, setGalleryCards] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [academics, setAcademics] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [homeImages, setHomeImages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [imgbbKey, setImgbbKey] = useState(() => localStorage.getItem('imgbb_api_key') || '');

  // Reorder Dirty States
  const [isCarouselDirty, setIsCarouselDirty] = useState(false);
  const [isHomeDirty, setIsHomeDirty] = useState(false);
  const [isGalleryDirty, setIsGalleryDirty] = useState(false);

  // Drag and Drop States & Handlers
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, index, type, list) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...list];
    const draggedItem = updated[draggedIndex];
    
    // Remove the dragged item
    updated.splice(draggedIndex, 1);
    // Insert the dragged item at drop index
    updated.splice(index, 0, draggedItem);

    if (type === 'carousel') {
      setCarouselImages(updated);
      setIsCarouselDirty(true);
    } else if (type === 'home') {
      setHomeImages(updated);
      setIsHomeDirty(true);
    } else if (type === 'gallery') {
      setGalleryCards(updated);
      setIsGalleryDirty(true);
    }

    setDraggedIndex(null);
  };

  // Edit States
  const [editId, setEditId] = useState(null);

  // Forms
  const [newNotice, setNewNotice] = useState({ Link: '', Heading: '', Description: '', Category: 'Announcements' });
  const [newCard, setNewCard] = useState({ MainImage: '', Description: '', Link: '', YoutubeLink: '', Category: 'Classroom Activities' });
  const [newCarouselImage, setNewCarouselImage] = useState('');
  const [newAcademic, setNewAcademic] = useState({ Name: '', Link: '', Category: 'Syllabus' });
  const [newDownload, setNewDownload] = useState({ Name: '', Link: '' });
  const [newHomeImage, setNewHomeImage] = useState({ imageLink: '', description: '', heading: '', category: 'General' });
  const [newContact, setNewContact] = useState({ Email: '', PhoneNo: '', Location: '' });
  const [registerAdmin, setRegisterAdmin] = useState({ username: '', password: '' });

  // Authenticate Admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  // Toast Auto-Close Timer
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Fetch all CMS lists
  const refreshData = () => {
    setLoading(true);
    const p1 = axios.get(`${API_BASE_URL}/GetNotice`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setNotices(raw.sort((a, b) => (b._id || '').localeCompare(a._id || '')));
      })
      .catch(() => setNotices([]));

    const p2 = axios.get(`${API_BASE_URL}/GetGalleryCard`)
      .then(res => setGalleryCards(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGalleryCards([]));

    const p3 = axios.get(`${API_BASE_URL}/GetCarouselImage`)
      .then(res => setCarouselImages(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCarouselImages([]));

    const p4 = axios.get(`${API_BASE_URL}/GetAcademicDropDown`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setAcademics(raw.sort((a, b) => (b._id || '').localeCompare(a._id || '')));
      })
      .catch(() => setAcademics([]));

    const p5 = axios.get(`${API_BASE_URL}/GetDownloadDropDown`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setDownloads(raw.sort((a, b) => (b._id || '').localeCompare(a._id || '')));
      })
      .catch(() => setDownloads([]));

    const p6 = axios.get(`${API_BASE_URL}/getHomeImages`)
      .then(res => {
        const raw = res.data?.data ?? res.data;
        setHomeImages(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setHomeImages([]));

    const p7 = axios.get(`${API_BASE_URL}/GetContactInfo`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setContacts(raw);
        if (raw[0]) {
          setNewContact({ Email: raw[0].Email, PhoneNo: raw[0].PhoneNo, Location: raw[0].Location });
        }
      })
      .catch(() => setContacts([]));

    const p8 = axios.get(`${API_BASE_URL}/GetApplications`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setApplications(raw.sort((a, b) => (b._id || '').localeCompare(a._id || '')));
      })
      .catch(() => setApplications([]));

    const p9 = axios.get(`${API_BASE_URL}/GetMessages`)
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setMessages(raw.sort((a, b) => (b._id || '').localeCompare(a._id || '')));
      })
      .catch(() => setMessages([]));

    Promise.all([p1, p2, p3, p4, p5, p6, p7, p8, p9]).finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshData();
  }, []);

  // Trigger Toast function
  const triggerToast = (msg) => {
    setToast({ message: msg });
  };

  // Trigger Confirmation Modal
  const askConfirmation = (msg, action) => {
    setConfirmDialog({
      message: msg,
      onConfirm: () => {
        setConfirmDialog(null);
        setActionLoading(true);
        action().finally(() => setActionLoading(false));
      },
      onCancel: () => {
        setConfirmDialog(null);
      }
    });
  };

  const handleLogout = () => {
    askConfirmation("Are you sure you want to log out of the CMS dashboard?", () => {
      localStorage.removeItem('token');
      navigate('/');
      return Promise.resolve();
    });
  };



  // Save full reordered sequence
  const saveSequenceOrder = (type) => {
    const action = () => {
      let list = [];
      let url = '';
      if (type === 'carousel') {
        list = carouselImages;
        url = `${API_BASE_URL}/ReorderCarouselImages`;
      } else if (type === 'home') {
        list = homeImages;
        url = `${API_BASE_URL}/ReorderHomeImages`;
      } else if (type === 'gallery') {
        list = galleryCards;
        url = `${API_BASE_URL}/ReorderGalleryCards`;
      }

      const orders = list.map((item, idx) => ({ _id: item._id, order: idx }));

      return axios.put(url, { orders })
        .then(() => {
          if (type === 'carousel') setIsCarouselDirty(false);
          if (type === 'home') setIsHomeDirty(false);
          if (type === 'gallery') setIsGalleryDirty(false);
          refreshData();
          triggerToast("Sequence order saved successfully");
        });
    };

    askConfirmation("Are you sure you want to save this new display sequence?", action);
  };

  // Publish Notice
  const handleAddNotice = (e) => {
    e.preventDefault();
    const action = () => {
      if (editId) {
        return axios.post(`${API_BASE_URL}/AddNewNotice`, newNotice)
          .then(() => {
            return axios.delete(`${API_BASE_URL}/DeleteNotice/${editId}`).then(() => {
              setEditId(null);
              setNewNotice({ Link: '', Heading: '', Description: '', Category: 'Announcements' });
              refreshData();
              triggerToast('Notice updated successfully!');
            });
          });
      } else {
        return axios.post(`${API_BASE_URL}/AddNewNotice`, newNotice)
          .then(() => {
            setNewNotice({ Link: '', Heading: '', Description: '', Category: 'Announcements' });
            refreshData();
            triggerToast('Notice published successfully!');
          });
      }
    };
    
    askConfirmation(editId ? "Save edits to this Notice?" : "Publish this new notice to the Notice Board?", action);
  };

  // Add Gallery Card
  const handleAddGalleryCard = (e) => {
    e.preventDefault();
    const action = () => {
      if (editId) {
        return axios.put(`${API_BASE_URL}/EditGalleryCard/${editId}`, newCard)
          .then(() => {
            setEditId(null);
            setNewCard({ MainImage: '', Description: '', Link: '', YoutubeLink: '', Category: 'Classroom Activities' });
            refreshData();
            triggerToast('Gallery Card updated successfully!');
          });
      } else {
        return axios.post(`${API_BASE_URL}/AddNewCard`, newCard)
          .then(() => {
            setNewCard({ MainImage: '', Description: '', Link: '', YoutubeLink: '', Category: 'Classroom Activities' });
            refreshData();
            triggerToast('Gallery card added successfully!');
          });
      }
    };

    askConfirmation(editId ? "Save edits to this Gallery Card?" : "Publish this new card to the scrapbook gallery?", action);
  };

  // Add Academic Dropdown
  const handleAddAcademic = (e) => {
    e.preventDefault();
    const action = () => {
      if (editId) {
        return axios.put(`${API_BASE_URL}/EditAcademicDropDown/${editId}`, newAcademic)
          .then(() => {
            setEditId(null);
            setNewAcademic({ Name: '', Link: '', Category: 'Syllabus' });
            refreshData();
            triggerToast('Academic Link updated successfully!');
          });
      } else {
        return axios.post(`${API_BASE_URL}/AddNewAcademicDropDown`, newAcademic)
          .then(() => {
            setNewAcademic({ Name: '', Link: '', Category: 'Syllabus' });
            refreshData();
            triggerToast('Academic link added successfully!');
          });
      }
    };

    askConfirmation(editId ? "Save edits to this Academic document link?" : "Link this academic document to the syllabus page?", action);
  };

  // Add Home Image
  const handleAddHomeImage = (e) => {
    e.preventDefault();
    const action = () => {
      if (editId) {
        return axios.put(`${API_BASE_URL}/EditHomeImage/${editId}`, newHomeImage)
          .then(() => {
            setEditId(null);
            setNewHomeImage({ imageLink: '', description: '', heading: '', category: 'General' });
            refreshData();
            triggerToast('Home Content updated successfully!');
          });
      } else {
        return axios.post(`${API_BASE_URL}/postHomeImage`, newHomeImage)
          .then(() => {
            setNewHomeImage({ imageLink: '', description: '', heading: '', category: 'General' });
            refreshData();
            triggerToast('Home content added successfully!');
          });
      }
    };

    askConfirmation(editId ? "Save edits to this campus feature card?" : "Add this new campus feature card to the homepage?", action);
  };

  // Save Contact Info
  const handleSaveContact = (e) => {
    e.preventDefault();
    const action = () => {
      if (contacts[0]) {
        return axios.put(`${API_BASE_URL}/UpdateContact/${contacts[0]._id}`, newContact)
          .then(() => { refreshData(); triggerToast('Contact details saved successfully!'); });
      } else {
        return axios.post(`${API_BASE_URL}/AddContactInfo`, newContact)
          .then(() => { refreshData(); triggerToast('Contact details created successfully!'); });
      }
    };

    askConfirmation("Update the school's official contact information details?", action);
  };

  const handleAddCarouselImage = (e) => {
    e.preventDefault();
    const action = () => {
      return axios.post(`${API_BASE_URL}/AddNewCarouselImage`, { Image: newCarouselImage })
        .then(() => { setNewCarouselImage(''); refreshData(); triggerToast('Carousel Highlight added successfully!'); });
    };
    askConfirmation("Add this image to the auto-sliding highlights carousel?", action);
  };

  const handleAddDownload = (e) => {
    e.preventDefault();
    const action = () => {
      return axios.post(`${API_BASE_URL}/AddNewDownloadDropDown`, newDownload)
        .then(() => { setNewDownload({ Name: '', Link: '' }); refreshData(); triggerToast('Downloadable form added successfully!'); });
    };
    askConfirmation("Add this document to the downloadable resources list?", action);
  };

  const handleRegisterAdmin = (e) => {
    e.preventDefault();
    const action = () => {
      return axios.post(`${API_BASE_URL}/Register`, registerAdmin)
        .then(() => { setRegisterAdmin({ username: '', password: '' }); triggerToast('New Admin account registered successfully!'); });
    };
    askConfirmation("Create a new administrator credential?", action);
  };

  const handleUpdateStatus = (id, status) => {
    const action = () => {
      return axios.put(`${API_BASE_URL}/UpdateApplicationStatus/${id}`, { status })
        .then((res) => {
          refreshData();
          setSelectedApp(res.data);
          triggerToast(`Application status updated to: ${status}`);
        });
    };
    askConfirmation(`Change application status to ${status}?`, action);
  };

  const handleDeleteApplication = (id) => {
    const action = () => {
      return axios.delete(`${API_BASE_URL}/DeleteApplication/${id}`)
        .then(() => {
          setSelectedApp(null);
          refreshData();
          triggerToast('Application deleted successfully.');
        });
    };
    askConfirmation("Permanently delete this student registration form?", action);
  };

  const handleDeleteItem = (url, id) => {
    const action = () => {
      return axios.delete(`${url}/${id}`)
        .then(() => { refreshData(); triggerToast('Item deleted successfully.'); });
    };
    askConfirmation("Are you sure you want to permanently delete this item?", action);
  };

  const tabs = [
    { name: 'Applications', icon: <ClipboardList size={18} /> },
    { name: 'Notice Board', icon: <FileText size={18} /> },
    { name: 'Carousel Highlights', icon: <ImageIcon size={18} /> },
    { name: 'Academics Program', icon: <LinkIcon size={18} /> },
    { name: 'Campus & Home Content', icon: <LayoutDashboard size={18} /> },
    { name: 'Gallery Cards', icon: <Compass size={18} /> },
    { name: 'Download Links', icon: <Download size={18} /> },
    { name: 'Contact Info & Admin', icon: <PhoneCall size={18} /> },
    { name: 'Messages', icon: <MessageSquare size={18} /> }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF7', display: 'flex', fontFamily: 'Quicksand' }}>
      
      {/* Sidebar Panel */}
      <aside style={{ width: '280px', backgroundColor: '#0b1d33', color: '#ffffff', padding: '32px 20px', display: 'flex', flexDirection: 'column', gap: '40px', position: 'fixed', height: '100vh', zIndex: 100 }}>
        <div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: 'var(--secondary-color)', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Poppins' }}>
            VTS Dashboard
          </h2>
          <p style={{ fontSize: '0.8rem', opacity: 0.7, margin: '4px 0 0 0', fontWeight: 700 }}>Central Management System</p>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
          {tabs.map((tb) => (
            <button
              key={tb.name}
              onClick={() => { setActiveTab(tb.name); setEditId(null); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: activeTab === tb.name ? 'rgba(250, 204, 21, 0.2)' : 'transparent',
                color: activeTab === tb.name ? 'var(--secondary-color)' : '#ffffff',
                fontSize: '0.92rem',
                fontWeight: 700,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)'
              }}
            >
              {tb.icon} {tb.name}
            </button>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            fontSize: '0.92rem',
            fontWeight: 700,
            cursor: 'pointer',
            marginTop: 'auto'
          }}
        >
          <LogOut size={18} /> Log Out
        </button>
      </aside>

      {/* Main Workspace Panel */}
      <main style={{ marginLeft: '280px', flex: 1, padding: '48px 40px', minWidth: 0 }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', borderBottom: '2px dashed var(--border-color)', paddingBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '2.25rem', color: 'var(--text-primary)', fontFamily: 'Poppins', fontWeight: 800, margin: 0 }}>{activeTab}</h1>
            <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontWeight: 700 }}>Manage details, sequence order, and publishing schedules.</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {(loading || actionLoading) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary-dark)', fontWeight: 800, fontSize: '0.9rem' }}>
                <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} /> Processing...
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowApiKeyModal(true)}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
                padding: '8px 14px',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <Key size={14} style={{ color: 'var(--primary-color)' }} />
              <span>ImgBB API: {imgbbKey ? 'Configured' : 'Set Key'}</span>
            </button>
            <span style={{ backgroundColor: 'var(--bg-academics)', color: 'var(--primary-dark)', padding: '8px 16px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 800 }}>Admin Authenticated</span>
          </div>
        </div>

        {/* Dynamic CMS Tabs View */}

        {/* 1. Admission Applications Tab */}
        {activeTab === 'Applications' && (
          <div>
            <div style={{ backgroundColor: '#ffffff', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid var(--border-color)' }}>
                    <th style={thStyle}>App Number</th>
                    <th style={thStyle}>Student Name</th>
                    <th style={thStyle}>Class</th>
                    <th style={thStyle}>Mobile Number</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((app) => (
                    <tr key={app._id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontWeight: 800, color: 'var(--primary-dark)' }}>{app.applicationNumber}</span></td>
                      <td style={tdStyle}><strong>{app.studentName}</strong></td>
                      <td style={tdStyle}>{app.classApplyingFor}</td>
                      <td style={tdStyle}>{app.mobileNumber}</td>
                      <td style={tdStyle}>
                        <span style={{ 
                          padding: '4px 12px', 
                          borderRadius: '12px', 
                          fontSize: '0.78rem', 
                          fontWeight: 800, 
                          color: app.status === 'Approved' ? '#15803d' : app.status === 'Rejected' ? '#b91c1c' : '#b45309',
                          backgroundColor: app.status === 'Approved' ? '#dcfce7' : app.status === 'Rejected' ? '#fee2e2' : '#fef3c7'
                        }}>
                          {app.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => setSelectedApp(app)} className="btn-premium btn-premium-primary" style={{ padding: '0 12px', height: '32px', fontSize: '0.8rem', borderRadius: '8px' }}>
                          <Eye size={12} /> View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 2. Notice Board Tab */}
        {activeTab === 'Notice Board' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            {/* Form */}
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins', fontWeight: 800 }}>{editId ? 'Edit Notice' : 'Publish New Notice'}</h3>
              <form onSubmit={handleAddNotice} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Notice Heading *</label>
                  <input type="text" value={newNotice.Heading} onChange={e => setNewNotice({ ...newNotice, Heading: e.target.value })} className="dashboard-input" placeholder="e.g. Annual Sports Meet Circular" required />
                </div>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select value={newNotice.Category} onChange={e => setNewNotice({ ...newNotice, Category: e.target.value })} className="dashboard-input" required>
                    <option value="Announcements">Announcements</option>
                    <option value="Holidays">Holidays</option>
                    <option value="Examinations">Examinations</option>
                    <option value="Events">Events</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>PDF Document URL (Optional)</label>
                  <input type="text" value={newNotice.Link} onChange={e => setNewNotice({ ...newNotice, Link: e.target.value })} className="dashboard-input" placeholder="Enter PDF link" />
                </div>
                <div>
                  <label style={labelStyle}>Brief Description (Optional)</label>
                  <textarea value={newNotice.Description} onChange={e => setNewNotice({ ...newNotice, Description: e.target.value })} className="dashboard-input" rows="4" placeholder="Brief circular content..." />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px', fontSize: '0.9rem' }}>
                  <Plus size={16} /> {editId ? 'Update Notice' : 'Publish Circular'}
                </button>
              </form>
            </div>

            {/* List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontFamily: 'Poppins', fontSize: '1.25rem' }}>Active Circulars</h3>
              {notices.map((nt) => (
                <div key={nt._id} style={itemCardStyle}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '1rem', fontFamily: 'Poppins' }}>{nt.Heading}</h4>
                    <span style={{ fontSize: '0.75rem', color: 'var(--accent-purple)', fontWeight: 800, textTransform: 'uppercase' }}>{nt.Category}</span>
                    <p style={{ margin: '4px 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{nt.Description || 'No description provided'}</p>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Created: {new Date(nt.DateCreated).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setEditId(nt._id); setNewNotice({ Link: nt.Link, Heading: nt.Heading, Description: nt.Description, Category: nt.Category }); }} style={{ ...iconBtnStyle, color: 'var(--primary-dark)' }}>
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDeleteItem(`${API_BASE_URL}/DeleteNotice`, nt._id)} style={{ ...iconBtnStyle, color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. Carousel Highlights Tab */}
        {activeTab === 'Carousel Highlights' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins' }}>Add Highlight Image</h3>
              <form onSubmit={handleAddCarouselImage} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ImageUploadCompressor 
                  label="Compress & Upload Carousel Image"
                  onImageUploaded={(url) => setNewCarouselImage(url)}
                />
                <div>
                  <label style={labelStyle}>Image URL *</label>
                  <input type="text" value={newCarouselImage} onChange={e => setNewCarouselImage(e.target.value)} className="dashboard-input" placeholder="Paste link or upload & compress above" required />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px' }}>
                  <Plus size={16} /> Add Carousel Image
                </button>
              </form>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontFamily: 'Poppins', fontSize: '1.25rem', margin: 0 }}>Current Carousel Sequence</h3>
                {isCarouselDirty && (
                  <button onClick={() => saveSequenceOrder('carousel')} className="btn-premium btn-premium-primary" style={{ height: '36px', padding: '0 16px', fontSize: '0.85rem' }}>
                    Save Carousel Order
                  </button>
                )}
              </div>
              {carouselImages.map((img, i) => (
                <div 
                  key={img._id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i, 'carousel', carouselImages)}
                  style={{ ...itemCardStyle, cursor: 'grab' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <img src={img.Image} alt="Carousel" style={{ width: '80px', height: '50px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border-color)', pointerEvents: 'none' }} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Sequence #{i + 1} <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 600 }}>(Drag to reorder)</span></span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => handleDeleteItem(`${API_BASE_URL}/DeleteCarouselImage`, img._id)} style={{ ...iconBtnStyle, color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. Academics Program Tab */}
        {activeTab === 'Academics Program' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins' }}>{editId ? 'Edit Academic Link' : 'Add Academic Document'}</h3>
              <form onSubmit={handleAddAcademic} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Document Title *</label>
                  <input type="text" value={newAcademic.Name} onChange={e => setNewAcademic({ ...newAcademic, Name: e.target.value })} className="dashboard-input" placeholder="e.g. Nursery Books list 2026" required />
                </div>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select value={newAcademic.Category} onChange={e => setNewAcademic({ ...newAcademic, Category: e.target.value })} className="dashboard-input" required>
                    <option value="Syllabus">Syllabus</option>
                    <option value="Books">Books</option>
                    <option value="Calendars">Calendars</option>
                    <option value="Schedules">Schedules</option>
                    <option value="Timetable">Timetable</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>File Document URL *</label>
                  <input type="text" value={newAcademic.Link} onChange={e => setNewAcademic({ ...newAcademic, Link: e.target.value })} className="dashboard-input" placeholder="Google Drive or PDF URL" required />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px' }}>
                  <Plus size={16} /> {editId ? 'Update Document' : 'Link Document'}
                </button>
              </form>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontFamily: 'Poppins', fontSize: '1.25rem' }}>Linked Documents</h3>
              {academics.map((ac) => (
                <div key={ac._id} style={itemCardStyle}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontFamily: 'Poppins' }}>{ac.Name}</h4>
                    <span style={{ fontSize: '0.75rem', color: 'var(--primary-dark)', fontWeight: 800, textTransform: 'uppercase' }}>{ac.Category || 'Syllabus'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setEditId(ac._id); setNewAcademic({ Name: ac.Name, Link: ac.Link, Category: ac.Category || 'Syllabus' }); }} style={{ ...iconBtnStyle, color: 'var(--primary-dark)' }}>
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDeleteItem(`${API_BASE_URL}/DeleteAcademicDropDown`, ac._id)} style={{ ...iconBtnStyle, color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 5. Campus & Home Content Tab */}
        {activeTab === 'Campus & Home Content' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins' }}>{editId ? 'Edit Home Image' : 'Add Campus Feature Image'}</h3>
              <form onSubmit={handleAddHomeImage} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Image Heading *</label>
                  <input type="text" value={newHomeImage.heading} onChange={e => setNewHomeImage({ ...newHomeImage, heading: e.target.value })} className="dashboard-input" placeholder="e.g. Science Laboratory" required />
                </div>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <input type="text" value={newHomeImage.category} onChange={e => setNewHomeImage({ ...newHomeImage, category: e.target.value })} className="dashboard-input" placeholder="e.g. Facilities" required />
                </div>
                <ImageUploadCompressor 
                  label="Compress & Upload Campus Feature Image"
                  onImageUploaded={(url) => setNewHomeImage(prev => ({ ...prev, imageLink: url }))}
                />
                <div>
                  <label style={labelStyle}>Image URL *</label>
                  <input type="text" value={newHomeImage.imageLink} onChange={e => setNewHomeImage({ ...newHomeImage, imageLink: e.target.value })} className="dashboard-input" placeholder="Paste campus image link or upload above" required />
                </div>
                <div>
                  <label style={labelStyle}>Description Details *</label>
                  <textarea value={newHomeImage.description} onChange={e => setNewHomeImage({ ...newHomeImage, description: e.target.value })} className="dashboard-input" rows="4" placeholder="Brief details about this feature..." required />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px' }}>
                  <Plus size={16} /> {editId ? 'Update Feature' : 'Save Feature Card'}
                </button>
              </form>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontFamily: 'Poppins', fontSize: '1.25rem', margin: 0 }}>Campus Features Sequence</h3>
                {isHomeDirty && (
                  <button onClick={() => saveSequenceOrder('home')} className="btn-premium btn-premium-primary" style={{ height: '36px', padding: '0 16px', fontSize: '0.85rem' }}>
                    Save Feature Order
                  </button>
                )}
              </div>
              {homeImages.map((img, i) => (
                <div 
                  key={img._id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i, 'home', homeImages)}
                  style={{ ...itemCardStyle, cursor: 'grab' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <img src={img.imageLink} alt="Home" style={{ width: '80px', height: '50px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border-color)', pointerEvents: 'none' }} />
                    <div>
                      <h4 style={{ margin: 0, fontSize: '0.95rem', fontFamily: 'Poppins' }}>{img.heading || 'Campus Feature'} <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 650 }}>(Drag to reorder)</span></h4>
                      <span style={{ fontSize: '0.75rem', color: 'var(--accent-coral)', fontWeight: 800 }}>{img.category || 'General'}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setEditId(img._id); setNewHomeImage({ imageLink: img.imageLink, description: img.description, heading: img.heading || '', category: img.category || 'General' }); }} style={{ ...iconBtnStyle, color: 'var(--primary-dark)' }}>
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDeleteItem(`${API_BASE_URL}/deleteHomeImage`, img._id)} style={{ ...iconBtnStyle, color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. Gallery Cards Tab */}
        {activeTab === 'Gallery Cards' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins' }}>{editId ? 'Edit Gallery Card' : 'Add Scrapbook Card'}</h3>
              <form onSubmit={handleAddGalleryCard} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ImageUploadCompressor 
                  label="Compress & Upload Thumbnail"
                  onImageUploaded={(url) => setNewCard(prev => ({ ...prev, MainImage: url }))}
                />
                <div>
                  <label style={labelStyle}>Thumbnail Image URL *</label>
                  <input type="text" value={newCard.MainImage} onChange={e => setNewCard({ ...newCard, MainImage: e.target.value })} className="dashboard-input" placeholder="Paste image address" required />
                </div>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select value={newCard.Category} onChange={e => setNewCard({ ...newCard, Category: e.target.value })} className="dashboard-input" required>
                    <option value="Annual Day">Annual Day</option>
                    <option value="Sports">Sports</option>
                    <option value="Celebrations">Celebrations</option>
                    <option value="Trips">Trips</option>
                    <option value="Art & Craft">Art & Craft</option>
                    <option value="Classroom Activities">Classroom Activities</option>
                    <option value="Functions">Functions</option>
                    <option value="Cultural Events">Cultural Events</option>
                    <option value="Competitions">Competitions</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Google Drive Album Link (Optional)</label>
                  <input type="text" value={newCard.Link} onChange={e => setNewCard({ ...newCard, Link: e.target.value })} className="dashboard-input" placeholder="Album link" />
                </div>
                <div>
                  <label style={labelStyle}>YouTube Presentation Link (Optional)</label>
                  <input type="text" value={newCard.YoutubeLink} onChange={e => setNewCard({ ...newCard, YoutubeLink: e.target.value })} className="dashboard-input" placeholder="YouTube video URL" />
                </div>
                <div>
                  <label style={labelStyle}>Description Details *</label>
                  <textarea value={newCard.Description} onChange={e => setNewCard({ ...newCard, Description: e.target.value })} className="dashboard-input" rows="4" placeholder="Brief event description details..." required />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px' }}>
                  <Plus size={16} /> {editId ? 'Update Gallery Card' : 'Save Scrapbook Card'}
                </button>
              </form>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontFamily: 'Poppins', fontSize: '1.25rem', margin: 0 }}>Gallery Scrapbook Cards</h3>
                {isGalleryDirty && (
                  <button onClick={() => saveSequenceOrder('gallery')} className="btn-premium btn-premium-primary" style={{ height: '36px', padding: '0 16px', fontSize: '0.85rem' }}>
                    Save Gallery Order
                  </button>
                )}
              </div>
              {galleryCards.map((crd, i) => (
                <div 
                  key={crd._id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i, 'gallery', galleryCards)}
                  style={{ ...itemCardStyle, cursor: 'grab' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <img src={crd.MainImage} alt="Gallery" style={{ width: '80px', height: '50px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border-color)', pointerEvents: 'none' }} />
                    <div>
                      <h4 style={{ margin: 0, fontSize: '0.95rem', fontFamily: 'Poppins' }}>{crd.Description ? crd.Description.substring(0, 30) + '...' : 'Scrapbook Card'} <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontWeight: 650 }}>(Drag to reorder)</span></h4>
                      <span style={{ fontSize: '0.75rem', color: 'var(--accent-purple)', fontWeight: 800 }}>{crd.Category || 'Classroom Activities'}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setEditId(crd._id); setNewCard({ MainImage: crd.MainImage, Description: crd.Description, Link: crd.Link || '', YoutubeLink: crd.YoutubeLink || '', Category: crd.Category || 'Classroom Activities' }); }} style={{ ...iconBtnStyle, color: 'var(--primary-dark)' }}>
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDeleteItem(`${API_BASE_URL}/DeleteGalleryCard`, crd._id)} style={{ ...iconBtnStyle, color: '#ef4444' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 7. Downloads Tab */}
        {activeTab === 'Download Links' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins' }}>Add Download Link</h3>
              <form onSubmit={handleAddDownload} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Document Name *</label>
                  <input type="text" value={newDownload.Name} onChange={e => setNewDownload({ ...newDownload, Name: e.target.value })} className="dashboard-input" placeholder="e.g. TC Request Application" required />
                </div>
                <div>
                  <label style={labelStyle}>File Download URL *</label>
                  <input type="text" value={newDownload.Link} onChange={e => setNewDownload({ ...newDownload, Link: e.target.value })} className="dashboard-input" placeholder="Enter Download link" required />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px' }}>
                  <Plus size={16} /> Add Download Link
                </button>
              </form>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontFamily: 'Poppins', fontSize: '1.25rem' }}>Active Downloads</h3>
              {downloads.map((dl) => (
                <div key={dl._id} style={itemCardStyle}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontFamily: 'Poppins' }}>{dl.Name}</h4>
                  </div>
                  <button onClick={() => handleDeleteItem(`${API_BASE_URL}/DeleteDownloadDropDown`, dl._id)} style={{ ...iconBtnStyle, color: '#ef4444' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 8. Contact Info & Admin Registry Tab */}
        {activeTab === 'Contact Info & Admin' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'start' }}>
            {/* Edit Contact Info */}
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins', fontWeight: 800 }}>Edit Contact Information</h3>
              <form onSubmit={handleSaveContact} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Official Email *</label>
                  <input type="email" value={newContact.Email} onChange={e => setNewContact({ ...newContact, Email: e.target.value })} className="dashboard-input" placeholder="email@school.com" required />
                </div>
                <div>
                  <label style={labelStyle}>Official Phone Number *</label>
                  <input type="text" value={newContact.PhoneNo} onChange={e => setNewContact({ ...newContact, PhoneNo: e.target.value })} className="dashboard-input" placeholder="e.g. 8982397404" required />
                </div>
                <div>
                  <label style={labelStyle}>School Address *</label>
                  <textarea value={newContact.Location} onChange={e => setNewContact({ ...newContact, Location: e.target.value })} className="dashboard-input" rows="3" placeholder="Enter school location address" required />
                </div>
                <button type="submit" className="btn-premium btn-premium-primary" style={{ height: '44px' }}>
                  Save Contact Information
                </button>
              </form>
            </div>

            {/* Admin Registry */}
            <div style={formCardStyle}>
              <h3 style={{ marginBottom: '20px', fontSize: '1.25rem', fontFamily: 'Poppins', fontWeight: 800 }}><UserPlus size={20} /> Register New Admin</h3>
              <form onSubmit={handleRegisterAdmin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Username *</label>
                  <input type="text" value={registerAdmin.username} onChange={e => setRegisterAdmin({ ...registerAdmin, username: e.target.value })} className="dashboard-input" placeholder="Enter admin username" required />
                </div>
                <div>
                  <label style={labelStyle}>Password *</label>
                  <input type="password" value={registerAdmin.password} onChange={e => setRegisterAdmin({ ...registerAdmin, password: e.target.value })} className="dashboard-input" placeholder="Enter password" required />
                </div>
                <button type="submit" className="btn-premium btn-premium-secondary" style={{ height: '44px' }}>
                  Create Administrator Account
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 9. Messages Tab */}
        {activeTab === 'Messages' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontFamily: 'Poppins', fontWeight: 800 }}>Inboxes & User Messages ({messages.length})</h3>
            </div>
            {messages.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontWeight: 700 }}>No messages found in database.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                {messages.map((msg) => (
                  <div key={msg._id} style={{ display: 'flex', flexDirection: 'column', padding: '20px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }} onClick={() => setSelectedMessage(msg)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-color)', backgroundColor: 'rgba(96, 165, 250, 0.15)', padding: '4px 10px', borderRadius: '20px' }}>
                        {msg.createdAt ? new Date(msg.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteItem(`${API_BASE_URL}/DeleteMessage`, msg._id);
                        }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', color: '#ef4444', transition: 'all 0.2s' }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)' }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', fontFamily: 'Poppins', fontWeight: 800 }}>{msg.name}</h4>
                      <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{msg.email}</p>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-primary)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.5', fontWeight: 600 }}>
                        {msg.message}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </main>

      {/* Admission Application Detail Drawer Popup Modal */}
      {selectedApp && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '16px', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontFamily: 'Poppins', fontWeight: 800 }}>Student Profile</h3>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--primary-dark)', fontWeight: 800 }}>ID: {selectedApp.applicationNumber}</span>
              </div>
              <button onClick={() => setSelectedApp(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
              <div>
                <h4 style={subHeaderStyle}>Student Information</h4>
                <p style={detailTextStyle}><strong>Full Name:</strong> {selectedApp.studentName}</p>
                <p style={detailTextStyle}><strong>Gender:</strong> {selectedApp.gender}</p>
                <p style={detailTextStyle}><strong>Date of Birth:</strong> {selectedApp.dob}</p>
                <p style={detailTextStyle}><strong>Computed Age:</strong> {selectedApp.age}</p>
                <p style={detailTextStyle}><strong>Grade Applied:</strong> {selectedApp.classApplyingFor}</p>
                <p style={detailTextStyle}><strong>Aadhaar Card:</strong> {selectedApp.aadhaar || 'N/A'}</p>
                <p style={detailTextStyle}><strong>Previous School:</strong> {selectedApp.previousSchool || 'None'}</p>
              </div>

              <div>
                <h4 style={subHeaderStyle}>Parent & Contact Details</h4>
                <p style={detailTextStyle}><strong>Father's Name:</strong> {selectedApp.fatherName}</p>
                <p style={detailTextStyle}><strong>Mother's Name:</strong> {selectedApp.motherName}</p>
                <p style={detailTextStyle}><strong>Occupation:</strong> {selectedApp.occupation || 'N/A'}</p>
                <p style={detailTextStyle}><strong>Contact Email:</strong> {selectedApp.email}</p>
                <p style={detailTextStyle}><strong>Phone Number:</strong> {selectedApp.mobileNumber}</p>
                <p style={detailTextStyle}><strong>WhatsApp Number:</strong> {selectedApp.whatsappNumber}</p>
                <p style={detailTextStyle}><strong>Transport Needed:</strong> {selectedApp.transportationRequired}</p>
                <p style={detailTextStyle}><strong>Residential Address:</strong> {selectedApp.address}</p>
              </div>
            </div>

            <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '24px' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-secondary)' }}>REFERRAL INFO & REMARKS</p>
              <p style={{ margin: 0, fontSize: '0.92rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                {selectedApp.referralSource ? `Referral: ${selectedApp.referralSource}. ` : ''}
                {selectedApp.additionalNotes || 'No remarks provided.'}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => handleUpdateStatus(selectedApp._id, 'Approved')} className="btn-premium btn-premium-primary" style={{ padding: '0 16px', height: '36px', fontSize: '0.85rem', backgroundColor: '#22c55e', borderColor: '#22c55e' }}>
                  <CheckCircle size={14} /> Approve Admission
                </button>
                <button onClick={() => handleUpdateStatus(selectedApp._id, 'Contacted')} className="btn-premium btn-premium-secondary" style={{ padding: '0 16px', height: '36px', fontSize: '0.85rem', color: '#1e3a8a' }}>
                  Mark Contacted
                </button>
                <button onClick={() => handleUpdateStatus(selectedApp._id, 'Rejected')} className="btn-premium" style={{ padding: '0 16px', height: '36px', fontSize: '0.85rem', backgroundColor: '#ef4444', color: '#ffffff', border: 'none', cursor: 'pointer', borderRadius: 'var(--border-radius-btn)' }}>
                  Reject
                </button>
              </div>
              <button onClick={() => handleDeleteApplication(selectedApp._id)} style={{ border: 'none', backgroundColor: 'transparent', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}>
                <Trash2 size={16} /> Delete Application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '16px', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'Poppins', fontWeight: 800 }}>User Message</h3>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
                  Received: {selectedMessage.createdAt ? new Date(selectedMessage.createdAt).toLocaleString() : 'N/A'}
                </span>
              </div>
              <button onClick={() => setSelectedMessage(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <X size={24} />
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={labelStyle}>Sender Name</label>
                <p style={detailTextStyle}>{selectedMessage.name}</p>
              </div>
              <div>
                <label style={labelStyle}>Email Address</label>
                <p style={{ ...detailTextStyle, color: 'var(--primary-dark)', textDecoration: 'underline' }}>
                  <a href={`mailto:${selectedMessage.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{selectedMessage.email}</a>
                </p>
              </div>
              <div>
                <label style={labelStyle}>Message</label>
                <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--text-primary)', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontWeight: 600 }}>
                  {selectedMessage.message}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button onClick={() => setSelectedMessage(null)} className="btn-premium btn-premium-secondary" style={{ padding: '0 20px', height: '40px', fontSize: '0.88rem' }}>Close</button>
              <button
                onClick={() => {
                  const id = selectedMessage._id;
                  setSelectedMessage(null);
                  handleDeleteItem(`${API_BASE_URL}/DeleteMessage`, id);
                }}
                className="btn-premium"
                style={{ padding: '0 20px', height: '40px', fontSize: '0.88rem', backgroundColor: '#ef4444', color: '#ffffff', border: 'none', cursor: 'pointer', borderRadius: 'var(--border-radius-btn)' }}
              >
                <Trash2 size={16} style={{ marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} /> Delete Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ImgBB API Key Modal */}
      {showApiKeyModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, maxWidth: '520px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Key size={22} style={{ color: 'var(--primary-color)' }} />
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontFamily: 'Poppins' }}>ImgBB API Configuration</h3>
              </div>
              <button onClick={() => setShowApiKeyModal(false)} style={iconBtnStyle}>
                <X size={18} />
              </button>
            </div>

            <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', marginBottom: '20px', lineHeight: 1.5 }}>
              Enter your free ImgBB API key to enable direct 1-click cloud uploads. When configured, compressed images will automatically upload to ImgBB and fill the image URL field in your forms.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>ImgBB API Key</label>
              <input
                type="text"
                value={imgbbKey}
                onChange={(e) => setImgbbKey(e.target.value)}
                placeholder="e.g. 3a7b8c9d0e1f2a3b..."
                className="dashboard-input"
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Don't have a key? Get one for free at <a href="https://api.imgbb.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)', fontWeight: 700 }}>api.imgbb.com</a>.
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              {localStorage.getItem('imgbb_api_key') && (
                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem('imgbb_api_key');
                    setImgbbKey('');
                    triggerToast('ImgBB API key removed.');
                  }}
                  className="btn-premium btn-premium-secondary"
                  style={{ padding: '0 16px', height: '40px', fontSize: '0.88rem', color: '#ef4444' }}
                >
                  Clear Key
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowApiKeyModal(false)}
                className="btn-premium btn-premium-secondary"
                style={{ padding: '0 16px', height: '40px', fontSize: '0.88rem' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (imgbbKey.trim()) {
                    localStorage.setItem('imgbb_api_key', imgbbKey.trim());
                    triggerToast('ImgBB API key saved successfully!');
                  } else {
                    localStorage.removeItem('imgbb_api_key');
                    triggerToast('ImgBB API key cleared.');
                  }
                  setShowApiKeyModal(false);
                }}
                className="btn-premium btn-premium-primary"
                style={{ padding: '0 20px', height: '40px', fontSize: '0.88rem' }}
              >
                Save Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Backdrop Blurred Confirmation Dialog Popup */}
      {confirmDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(11,29,51,0.5)', backdropFilter: 'blur(8px)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#ffffff', padding: '32px', borderRadius: 'var(--border-radius-card)', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '1.25rem', fontFamily: 'Poppins', fontWeight: 800, marginBottom: '12px' }}>Confirm Action</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontWeight: 700, fontSize: '0.95rem' }}>{confirmDialog.message}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={confirmDialog.onCancel} className="btn-premium btn-premium-secondary" style={{ padding: '0 20px', height: '40px', fontSize: '0.88rem' }}>Cancel</button>
              <button onClick={confirmDialog.onConfirm} className="btn-premium btn-premium-primary" style={{ padding: '0 20px', height: '40px', fontSize: '0.88rem' }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-closing Toast notification bottom-right corner */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', backgroundColor: '#0b1d33', color: '#ffffff', padding: '16px 24px', borderRadius: '12px', boxShadow: 'var(--shadow-lg)', display: 'flex', alignItems: 'center', gap: '10px', zIndex: 10000, border: '1px solid rgba(255,255,255,0.1)' }}>
          <CheckCircle size={18} style={{ color: 'var(--accent-green)' }} />
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{toast.message}</span>
        </div>
      )}

    </div>
  );
}

// Table Styles
const thStyle = {
  padding: '16px 24px',
  color: 'var(--text-secondary)',
  fontWeight: 800,
  fontSize: '0.85rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tdStyle = {
  padding: '16px 24px',
  color: 'var(--text-primary)',
  fontSize: '0.92rem',
  fontWeight: 600
};

// Layout Cards
const formCardStyle = {
  backgroundColor: '#ffffff',
  padding: '36px 30px',
  borderRadius: 'var(--border-radius-card)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-md)'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  marginBottom: '6px',
  textTransform: 'uppercase'
};

const itemCardStyle = {
  backgroundColor: '#ffffff',
  padding: '16px 20px',
  borderRadius: '16px',
  border: '1px solid var(--border-color)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: 'var(--shadow-sm)'
};

const iconBtnStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  transition: 'all 0.2s ease'
};



// Modal details
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(11,29,51,0.5)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const modalContentStyle = {
  backgroundColor: '#ffffff',
  width: '100%',
  maxWidth: '700px',
  borderRadius: 'var(--border-radius-card)',
  boxShadow: 'var(--shadow-xl)',
  padding: '40px',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const subHeaderStyle = {
  margin: '0 0 12px 0',
  fontSize: '1.05rem',
  color: 'var(--primary-dark)',
  borderBottom: '2px dashed var(--border-color)',
  paddingBottom: '6px',
  fontFamily: 'Poppins'
};

const detailTextStyle = {
  margin: '0 0 6px 0',
  fontSize: '0.92rem',
  color: 'var(--text-secondary)'
};

// Add stylesheet triggers for input fields
const globalInputStyle = document.createElement('style');
globalInputStyle.innerHTML = `
  .dashboard-input {
    width: 100%;
    padding: 12px 16px;
    font-size: 0.95rem;
    font-weight: 700;
    font-family: 'Quicksand', sans-serif;
    border: 2px solid var(--border-color);
    border-radius: 12px;
    background-color: #f8fafc;
    color: var(--text-primary);
    outline: none;
    transition: all 0.25s ease;
  }
  .dashboard-input:focus {
    border-color: var(--primary-color) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.15) !important;
  }
`;
document.head.appendChild(globalInputStyle);
